// src/context/AgentChatContext.jsx
// Global state for the AI Chat Assistant (Phase 3).
// Manages: panel open/close, conversations list, active conversation,
// messages, streaming status, and auto-detected page context.

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { resolvePageContext } from '../utils/agentChatHelpers';

const AgentChatContext = createContext(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export function AgentChatProvider({ children }) {
  const { firebaseUser, teamOwnerId } = useAuth();
  const location = useLocation();

  // Resolved ownerId — matches Firestore rule: isOwnerOrTeamMember(resource.data.ownerId)
  const ownerId = teamOwnerId || firebaseUser?.uid;

  // Panel state
  const [isOpen, setIsOpen] = useState(false);

  // Conversations list (from Firestore)
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  // Active conversation
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(null);

  // Messages for active conversation
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentTool, setCurrentTool] = useState(null);
  const [streamingMessage, setStreamingMessage] = useState('');

  // Error
  const [error, setError] = useState(null);

  // Auto-detect page context from route
  const pageContext = useMemo(() => resolvePageContext(location.pathname), [location.pathname]);

  // ─── Load conversation list ──────────────────────────────────────────────
  useEffect(() => {
    if (!ownerId) {
      setConversations([]);
      return;
    }

    setConversationsLoading(true);
    // Query by ownerId to satisfy Firestore rule: isOwnerOrTeamMember(resource.data.ownerId)
    const q = query(
      collection(db, 'agentChatConversations'),
      where('ownerId', '==', ownerId),
      orderBy('lastMessageAt', 'desc'),
      limit(20)
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setConversations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setConversationsLoading(false);
      },
      (err) => {
        console.error('Failed to load chat conversations:', err);
        setConversationsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [ownerId]);

  // ─── Load messages for active conversation ───────────────────────────────
  useEffect(() => {
    if (!activeConversationId || !firebaseUser?.uid) {
      setMessages([]);
      return;
    }

    setMessagesLoading(true);
    const q = query(
      collection(db, 'agentChatConversations', activeConversationId, 'messages'),
      orderBy('createdAt', 'asc'),
      limit(200)
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setMessagesLoading(false);
      },
      (err) => {
        console.error('Failed to load chat messages:', err);
        setMessagesLoading(false);
      }
    );

    return () => unsubscribe();
  }, [activeConversationId, firebaseUser?.uid]);

  // ─── Actions ─────────────────────────────────────────────────────────────

  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);

  const startNewConversation = useCallback(() => {
    setActiveConversationId(null);
    setActiveSessionId(null);
    setMessages([]);
    setStreamingMessage('');
    setError(null);
  }, []);

  const selectConversation = useCallback(
    (convId) => {
      const conv = conversations.find((c) => c.id === convId);
      setActiveConversationId(convId);
      setActiveSessionId(conv?.anthropicSessionId || null);
      setStreamingMessage('');
      setError(null);
    },
    [conversations]
  );

  // ─── Context Value ───────────────────────────────────────────────────────

  const value = useMemo(
    () => ({
      // Panel
      isOpen,
      openChat,
      closeChat,
      // Conversations
      conversations,
      conversationsLoading,
      activeConversationId,
      activeSessionId,
      selectConversation,
      startNewConversation,
      // Messages
      messages,
      messagesLoading,
      // Streaming
      isStreaming,
      setIsStreaming,
      currentTool,
      setCurrentTool,
      streamingMessage,
      setStreamingMessage,
      // Session tracking
      setActiveConversationId,
      setActiveSessionId,
      // Context
      pageContext,
      // Error
      error,
      setError,
    }),
    [
      isOpen,
      openChat,
      closeChat,
      conversations,
      conversationsLoading,
      activeConversationId,
      activeSessionId,
      selectConversation,
      startNewConversation,
      messages,
      messagesLoading,
      isStreaming,
      currentTool,
      streamingMessage,
      pageContext,
      error,
    ]
  );

  return <AgentChatContext.Provider value={value}>{children}</AgentChatContext.Provider>;
}

export function useAgentChatContext() {
  const context = useContext(AgentChatContext);
  if (!context) throw new Error('useAgentChatContext must be used within AgentChatProvider');
  return context;
}
